import { Box, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { ChartCard } from '../components/Cards/ChartCard';
import { useTaskCharts } from './hooks/useTaskCharts';
import { BarChart, LineChart } from '../components/Charts';
import { ChartTitle } from '@views/pages/Dashboard/components/ChartTitle';
import { SortByActionButton } from '@components';
import { theme } from '@store';

export const TaskCharts = () => {
  const {
    hasViewPermission,
    trackedMinutes,
    averageTrackedMinutes,
    createdTasks,
    openTasks,
    assignedTasks,
    mediumPriorityTasks,
    lowPriorityTasks,
    highPriorityTasks,
    urgentTasks,
    prematurelyCompletedTasks,
    uniqueTaskEdits,
    completedTasks,
    tasksByPriority,
    overdueTasks,
    taskMetrics,
    taskMetricsLinkedReportId,
    taskKeyToLabel,
    taskYAxisMaxValue,
    tasksChartSortByKey,
    handleChangeSortByKey,
    fullReportUrl,
    isLoadingTaskMetrics,
    isLoadingMinutes,
    isLoadingAvgMinutes,
    isLoadingAssigned,
    isLoadingCompleted,
    isLoadingByPriority,
    isLoadingOverdue,
    isLoadingUrgent,
    isLoadingMedium,
    isLoadingLow,
    isLoadingHigh,
    isLoadingUnique,
    isLoadingPremature,
    isLoadingOpen,
    isLoadingCreated,
  } = useTaskCharts();

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <ChartCard
          title={<ChartTitle title='openedVsCompletedTasks' />}
          report_id={taskMetricsLinkedReportId}
          isLoading={isLoadingTaskMetrics}
          actionButton={
            <Button
              disabled={!taskMetricsLinkedReportId}
              component={Link}
              to={fullReportUrl}
              target='_blank'
            >
              VIEW FULL REPORT
            </Button>
          }
        >
          <LineChart
            chartData={taskMetrics}
            labelKeys={taskKeyToLabel}
            taskYAxisMaxValue={taskYAxisMaxValue}
          />
        </ChartCard>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <ChartCard
          title={<ChartTitle title='taskMinutes' />}
          report_id={trackedMinutes?.linked_report_id}
          hideButton={!hasViewPermission}
          isLoading={isLoadingMinutes}
          actionButton={
            <SortByActionButton
              order={tasksChartSortByKey['minutes']}
              onClick={() => handleChangeSortByKey('minutes')}
            />
          }
        >
          <BarChart
            entries={
              trackedMinutes?.data.map(o => ({
                'Activity log': o.activity_minutes,
                'Care notes': o.care_note_minutes,
                label: o.name,
              })) ?? []
            }
            report_id={trackedMinutes?.linked_report_id}
            total={trackedMinutes?.total}
          />
        </ChartCard>
        <ChartCard
          title={<ChartTitle title='averageTaskMinutes' />}
          report_id={averageTrackedMinutes?.linked_report_id}
          hideButton={!hasViewPermission}
          isLoading={isLoadingAvgMinutes}
          actionButton={
            <SortByActionButton
              order={tasksChartSortByKey['minutes']}
              onClick={() => handleChangeSortByKey('minutes')}
            />
          }
        >
          <BarChart
            entries={
              averageTrackedMinutes?.data.map(o => ({
                'Activity log': o.activity_minutes,
                'Care notes': o.care_note_minutes,
                label: o.name,
              })) ?? []
            }
            report_id={averageTrackedMinutes?.linked_report_id}
            total={averageTrackedMinutes?.total}
          />
        </ChartCard>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          <ChartCard
            title={<ChartTitle title='assignedTask' />}
            report_id={assignedTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingAssigned}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['assignedTasks']}
                onClick={() => handleChangeSortByKey('assignedTasks')}
              />
            }
          >
            <BarChart
              report_id={assignedTasks?.linked_report_id}
              entries={
                assignedTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={assignedTasks?.total}
            />
          </ChartCard>

          <ChartCard
            title={<ChartTitle title='completedTasks' />}
            report_id={completedTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingCompleted}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['completedTasks']}
                onClick={() => handleChangeSortByKey('completedTasks')}
              />
            }
          >
            <BarChart
              report_id={completedTasks?.linked_report_id}
              entries={
                completedTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={completedTasks?.total}
            />
          </ChartCard>
        </Box>

        <ChartCard
          title={<ChartTitle title='taskByPriority' />}
          report_id={tasksByPriority?.linked_report_id}
          hideButton={!hasViewPermission}
          isLoading={isLoadingByPriority}
          actionButton={
            <SortByActionButton
              order={tasksChartSortByKey['taskByPriority']}
              onClick={() => handleChangeSortByKey('taskByPriority')}
            />
          }
        >
          <BarChart
            report_id={tasksByPriority?.linked_report_id}
            entries={
              tasksByPriority?.data.map(o => ({
                Overdue: o.overdue,
                Urgent: o.urgent,
                'High priority': o.high_priority,
                'Medium priority': o.medium_priority,
                label: o.name,
              })) ?? []
            }
          />
        </ChartCard>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          <ChartCard
            title={<ChartTitle title='overdueTasks' />}
            report_id={overdueTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingOverdue}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['overdueTasks']}
                onClick={() => handleChangeSortByKey('overdueTasks')}
              />
            }
          >
            <BarChart
              report_id={overdueTasks?.linked_report_id}
              entries={
                overdueTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={overdueTasks?.total}
            />
          </ChartCard>

          <ChartCard
            title={<ChartTitle title='urgentTasks' />}
            report_id={urgentTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingUrgent}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['urgentTasks']}
                onClick={() => handleChangeSortByKey('urgentTasks')}
              />
            }
          >
            <BarChart
              report_id={urgentTasks?.linked_report_id}
              entries={
                urgentTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={urgentTasks?.total}
            />
          </ChartCard>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          <ChartCard
            title={<ChartTitle title='highPriorityTasks' />}
            report_id={highPriorityTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingHigh}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['highPriorityTasks']}
                onClick={() => handleChangeSortByKey('highPriorityTasks')}
              />
            }
          >
            <BarChart
              report_id={highPriorityTasks?.linked_report_id}
              entries={
                highPriorityTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={highPriorityTasks?.total}
            />
          </ChartCard>

          <ChartCard
            title={<ChartTitle title='mediumPriorityTasks' />}
            report_id={mediumPriorityTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingMedium}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['mediumPriorityTasks']}
                onClick={() => handleChangeSortByKey('mediumPriorityTasks')}
              />
            }
          >
            <BarChart
              report_id={mediumPriorityTasks?.linked_report_id}
              entries={
                mediumPriorityTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={mediumPriorityTasks?.total}
            />
          </ChartCard>
        </Box>

        <ChartCard
          title={<ChartTitle title='lowPriorityTasks' />}
          report_id={lowPriorityTasks?.linked_report_id}
          hideButton={!hasViewPermission}
          isLoading={isLoadingLow}
          actionButton={
            <SortByActionButton
              order={tasksChartSortByKey['lowPriorityTasks']}
              onClick={() => handleChangeSortByKey('lowPriorityTasks')}
            />
          }
        >
          <BarChart
            report_id={lowPriorityTasks?.linked_report_id}
            entries={
              lowPriorityTasks?.data.map(o => ({
                'Task count': o.value,
                label: o.name,
              })) ?? []
            }
            total={lowPriorityTasks?.total}
          />
        </ChartCard>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          <ChartCard
            title={<ChartTitle title='uniqueTaskEdits' />}
            report_id={uniqueTaskEdits?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingUnique}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['uniqueTasks']}
                onClick={() => handleChangeSortByKey('uniqueTasks')}
              />
            }
          >
            <BarChart
              report_id={uniqueTaskEdits?.linked_report_id}
              entries={
                uniqueTaskEdits?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={uniqueTaskEdits?.total}
            />
          </ChartCard>

          <ChartCard
            title={<ChartTitle title='prematurelyClosedTasks' />}
            report_id={prematurelyCompletedTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingPremature}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['prematurelyClosedTasks']}
                onClick={() => handleChangeSortByKey('prematurelyClosedTasks')}
              />
            }
          >
            <BarChart
              report_id={prematurelyCompletedTasks?.linked_report_id}
              entries={
                prematurelyCompletedTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={prematurelyCompletedTasks?.total}
            />
          </ChartCard>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            [theme.breakpoints.down('lg')]: {
              flexDirection: 'column',
            },
          }}
        >
          <ChartCard
            title={<ChartTitle title='openTasks' />}
            report_id={openTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingOpen}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['openTasks']}
                onClick={() => handleChangeSortByKey('openTasks')}
              />
            }
          >
            <BarChart
              report_id={openTasks?.linked_report_id}
              entries={
                openTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={openTasks?.total}
            />
          </ChartCard>

          <ChartCard
            title={<ChartTitle title='createdTasks' />}
            report_id={createdTasks?.linked_report_id}
            hideButton={!hasViewPermission}
            isLoading={isLoadingCreated}
            actionButton={
              <SortByActionButton
                order={tasksChartSortByKey['createdTasks']}
                onClick={() => handleChangeSortByKey('createdTasks')}
              />
            }
          >
            <BarChart
              report_id={createdTasks?.linked_report_id}
              entries={
                createdTasks?.data.map(o => ({
                  'Task count': o.value,
                  label: o.name,
                })) ?? []
              }
              total={createdTasks?.total}
            />
          </ChartCard>
        </Box>
      </Box>
    </>
  );
};
